import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const StickyCartButton = ({ 
  product = {}, 
  selectedOptions = {}, 
  quantity = 1, 
  onAddToCart,
  isAddingToCart = false 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Show sticky button when user scrolls past the main product info
      const productInfoHeight = 800; // Approximate height of product info section
      setIsVisible(scrollTop > productInfoHeight);
      
      // Hide when near bottom to avoid overlap with footer
      const nearBottom = scrollTop + windowHeight >= documentHeight - 100;
      setIsAtBottom(nearBottom);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial state
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleAddToCart = () => {
    if (onAddToCart) {
      onAddToCart({
        product,
        selectedOptions,
        quantity
      });
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const finalPrice = product.discountedPrice || product.originalPrice || 0;
  const isOutOfStock = product.availability === 'out-of-stock';

  if (!isVisible || isAtBottom) {
    return null;
  }

  return (
    <div className="md:hidden fixed bottom-0 left-0 right-0 bg-surface border-t border-border shadow-lg z-40 animate-slide-up">
      <div className="px-4 py-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon name="Package" size={20} className="text-text-secondary" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-medium text-text-primary truncate text-sm">
                {product.name}
              </h3>
              <div className="flex items-center space-x-2">
                <span className="font-semibold text-primary">
                  {formatPrice(finalPrice)}
                </span>
                {product.discount > 0 && (
                  <span className="text-xs text-text-secondary line-through">
                    {formatPrice(product.originalPrice)}
                  </span>
                )}
              </div>
            </div>
          </div>
          
          <div className="text-right">
            <div className="text-xs text-text-secondary">Qty: {quantity}</div>
            {Object.keys(selectedOptions).length > 0 && (
              <div className="text-xs text-text-secondary">
                {Object.entries(selectedOptions).map(([key, value]) => (
                  <span key={key}>{value}</span>
                )).join(', ')}
              </div>
            )}
          </div>
        </div>

        <div className="flex space-x-3">
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            iconName="Heart"
            iconPosition="left"
          >
            Wishlist
          </Button>
          
          <Button
            variant="default"
            size="sm"
            className="flex-1"
            onClick={handleAddToCart}
            disabled={isOutOfStock}
            loading={isAddingToCart}
            iconName="ShoppingCart"
            iconPosition="left"
          >
            {isOutOfStock ? 'Out of Stock' : 'Add to Cart'}
          </Button>
        </div>

        {/* Stock Status */}
        {product.availability === 'low-stock' && (
          <div className="mt-2 flex items-center justify-center space-x-1 text-xs text-warning">
            <Icon name="AlertTriangle" size={12} />
            <span>Only {product.stockCount} left in stock</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default StickyCartButton;